/**
 * Login 2FA Modal
 *
 * Modal para ingresar código 2FA durante el proceso de login.
 * Aparece después de verificar credenciales cuando el usuario tiene 2FA habilitado.
 */

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Shield, Loader2, AlertCircle, KeyRound } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/presentation/components/ui/Dialog'
import { Input } from '@/presentation/components/ui/Input'
import { Button } from '@/presentation/components/ui/Button'
import { tokenStorage } from '@/lib/api'
import { useComplete2FALogin } from '@/application/auth/useTwoFactor'

// =============================================================================
// Schema
// =============================================================================

const verifySchema = z.object({
  code: z.string().min(6, 'El código debe tener al menos 6 caracteres').max(10),
})

type VerifyForm = z.infer<typeof verifySchema>

// =============================================================================
// Types
// =============================================================================

interface Login2FAModalProps {
  open: boolean
  userId: string
  userEmail: string
  onSuccess: () => void
  onCancel: () => void
}

// =============================================================================
// Component
// =============================================================================

export function Login2FAModal({
  open,
  userId,
  userEmail,
  onSuccess,
  onCancel,
}: Login2FAModalProps) {
  const [error, setError] = useState<string | null>(null)
  const complete2FAMutation = useComplete2FALogin()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VerifyForm>({
    resolver: zodResolver(verifySchema),
  })

  const onSubmit = async (data: VerifyForm) => {
    setError(null)

    try {
      const result = await complete2FAMutation.mutateAsync({
        userId,
        code: data.code,
      })

      // Store tokens after successful 2FA verification
      tokenStorage.setTokens(result.accessToken, result.refreshToken)

      // Reset form and notify success
      reset()
      onSuccess()
    } catch (err: any) {
      const message =
        err.response?.data?.message || err.message || 'Código inválido'
      setError(message)
    }
  }

  const handleCancel = () => {
    reset()
    setError(null)
    onCancel()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[var(--primary)]" />
            Verificación de Dos Factores
          </DialogTitle>
          <DialogDescription>
            Ingresa el código de tu aplicación de autenticación para continuar
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* User info */}
          <div className="p-3 rounded-lg bg-[var(--background-subtle)]">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-[var(--primary)]/10">
                <KeyRound className="h-4 w-4 text-[var(--primary)]" />
              </div>
              <div>
                <p className="text-sm text-[var(--foreground-muted)]">
                  Iniciando sesión como
                </p>
                <p className="font-medium text-[var(--foreground)]">
                  {userEmail}
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-[var(--destructive)]/10 border border-[var(--destructive)]/20 text-[var(--destructive)]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <Input
            label="Código de verificación"
            placeholder="Ingresa el código de 6 dígitos"
            maxLength={10}
            className="text-center text-xl tracking-widest font-mono"
            error={errors.code?.message}
            autoFocus
            {...register('code')}
          />

          <p className="text-xs text-[var(--foreground-muted)] text-center">
            Ingresa el código de tu aplicación de autenticación (Google
            Authenticator, Authy, etc.) o uno de tus códigos de respaldo.
          </p>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={handleCancel}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={complete2FAMutation.isPending}
            >
              {complete2FAMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Verificando...
                </>
              ) : (
                'Verificar'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
